import React, { useRef, useState } from 'react';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import TimePicker from 'react-multi-date-picker/plugins/time_picker';
import { PrimaryButton, SecondaryButton } from '../buttonComponent/ButtonComponent';
import { ReactComponent as CalendarIcon } from '../buttonComponent/img/calendar.svg';
import './DateRangePicker.css';
import './DateTimePicker.css';

/**
 * The wire format. A wall-clock instant with no zone attached — the caller decides what zone it
 * means, and the O/D tape means the agency's.
 *
 * Everything here goes through `DateObject`'s own year/month/day/hour/minute/second fields and
 * never through a JS `Date` or an epoch, so the browser's zone is not involved at any point.
 * A round-trip through `Date` would be a real bug rather than a nicety: a wall time that does not
 * exist locally — 02:30 on a spring-forward morning — is silently shifted an hour, and the value
 * the reader typed is not the value that comes back.
 */
const WIRE = 'YYYY-MM-DDTHH:mm:ss';

/**
 * The same instant with a space, which is the only shape react-date-object will read.
 *
 * Its parser has no escape for a literal character, so the `T` swallows the `HH` beside it and
 * the whole value comes back invalid — silently, as an empty picker. Swapping the separator at
 * this boundary keeps `WIRE` exactly as the callers and `datetime-local` both write it.
 */
const PARSE = 'YYYY-MM-DD HH:mm:ss';

/** Readable in the trigger, and the same order the tape's own clock readout uses. */
const DISPLAY = 'DD-MM-YYYY HH:mm:ss';

/** `timeOnly`: the date is context the caller already knows, so only the clock is shown. */
const DISPLAY_TIME = 'HH:mm:ss';

const toDateObject = (value) => {
    if (!value) return null;
    const parsed = new DateObject({ date: String(value).replace('T', ' '), format: PARSE });
    return parsed.isValid ? parsed : null;
};

const toWire = (dateObject) => (dateObject ? dateObject.format(PARSE).replace(' ', 'T') : '');

/** rmdp clones this with `openCalendar`; everything else is ours. */
function DateTimeTrigger({
    openCalendar, displayValue, placeholder, ariaLabel, compact, invalid,
}) {
    return (
        <button
          type="button"
          className={`horizon-datetime-trigger${compact ? ' horizon-datetime-trigger-compact' : ''}${invalid ? ' horizon-datetime-trigger-invalid' : ''}`}
          onClick={openCalendar}
          aria-label={ariaLabel}
        >
            <CalendarIcon className="horizon-datetime-icon" aria-hidden="true" />
            <span className={displayValue ? undefined : 'horizon-datetime-placeholder'}>
                {displayValue || placeholder}
            </span>
        </button>
    );
}

/** Cancel/Apply, the same footer the range and single pickers commit through. */
function DateTimeFooter({
    position: _position, onCancel, onApply, applyDisabled,
}) {
    return (
        <div className="date-range-custom-footer horizon-datetime-footer">
            <div className="date-range-button">
                <SecondaryButton label="Cancel" width="5rem" onClickFunction={onCancel} />
                <PrimaryButton label="Apply" width="5rem" onClickFunction={onApply} disable={applyDisabled} />
            </div>
        </div>
    );
}

/**
 * One date **and** time, on the Horizon calendar.
 *
 * Built because there was no Horizon control for this. `DateRangePicker` is a range over whole
 * days and `SingleDatePicker`'s footer time input is `step={60}`, so neither can express the
 * second a scrubber needs — which left `<input type="datetime-local">` and the browser's own
 * popup, in its own colours, in the middle of a Horizon panel.
 *
 * The calendar is the same `react-multi-date-picker` the other two use, so the month grid,
 * arrows and weekday header inherit `DateRangePicker.css` unchanged. Only what that file has no
 * reason to carry is added here: `.rmdp-selected` (it styles `.rmdp-range`, and a single pick is
 * not a range, so the day would come back in the library's default blue) and the time columns.
 *
 * @param {string} value  Wall-clock `YYYY-MM-DDTHH:mm:ss`, or '' for empty.
 * @param {function} onChange  Called with the same shape on Apply.
 * @param {string} [min] [max]  Selectable day bounds, same shape. The clock is not bounded —
 *   callers already validate the instant, and a half-disabled spinner reads as broken.
 * @param {boolean} [compact]  36px trigger for a toolbar rather than the 44px filter-bar control.
 * @param {boolean} [timeOnly]  Hide the calendar and pick the clock alone. The value is still a
 *   full instant — the date rides along unchanged from the current value, or from `min` when
 *   there is none — so callers keep one shape whichever half is on show.
 */
const DateTimePicker = (props) => {
    const {
        value, onChange, min, max, ariaLabel, placeholder, compact, invalid, timeOnly,
    } = props;

    const pickerRef = useRef();
    const [draft, setDraft] = useState(null);

    /**
     * Whether the close about to happen is the one a day click causes.
     *
     * rmdp closes the calendar as soon as a day is picked — which for a *date-time* picker puts
     * the clock and the Apply button out of reach the moment you choose the date. `onClose` can
     * veto that by returning false, so the veto is scoped to exactly that close: the flag is
     * raised on selection and dropped on the next tick, leaving a later outside click or Escape
     * to dismiss the popup normally.
     */
    const selectingRef = useRef(false);

    const committed = toDateObject(value);
    const display = timeOnly ? DISPLAY_TIME : DISPLAY;
    const emptyLabel = placeholder || (timeOnly ? 'Select time' : 'Select date & time');
    /**
     * What the clock opens on when the field is empty.
     *
     * With no calendar there is nothing to click to give the value a date, so an empty time-only
     * picker would have no instant to edit and Apply would stay dead. The lower bound is the
     * caller's own anchor — for the trip tape, the moment the trip began.
     */
    const seed = committed || (timeOnly ? toDateObject(min) : null);

    const select = (next) => {
        selectingRef.current = true;
        setDraft(next);
        setTimeout(() => { selectingRef.current = false; }, 0);
    };

    const close = () => {
        selectingRef.current = false;
        pickerRef.current?.closeCalendar();
    };

    const apply = () => {
        onChange(toWire(draft));
        close();
    };

    return (
        <div className="horizon-datetime-root">
            <DatePicker
              ref={pickerRef}
              /**
               * The draft while open, the committed value when closed — never `committed` alone.
               *
               * rmdp falls back to *now* when its value is empty, so an unset field opened its
               * clock on the current time of day instead of the caller's anchor, and the first
               * spin committed an instant on today's date. With no calendar on show there was
               * nothing to reveal the wrong day: the tape's "Jump to" read 16:15:04 for a trip
               * that ran 10:00–11:00, and jumping then failed as outside the trip.
               */
              value={draft || committed}
              // The draft is what the footer commits, so opening on a cleared field and cancelling
              // cannot leave a half-made selection behind.
              onOpen={() => setDraft(seed)}
              disableDayPicker={Boolean(timeOnly)}
              onChange={select}
              onClose={() => {
                  if (selectingRef.current) return false;
                  setDraft(null);
                  return undefined;
              }}
              minDate={toDateObject(min) || undefined}
              maxDate={toDateObject(max) || undefined}
              format={display}
              arrow={false}
              offsetY={8}
              numberOfMonths={1}
              weekStartDayIndex={1}
              className={`custom horizon-datetime-panel${timeOnly ? ' horizon-datetime-panel-time' : ''}`}
              render={(
                  <DateTimeTrigger
                    displayValue={committed ? committed.format(display) : ''}
                    placeholder={emptyLabel}
                    ariaLabel={ariaLabel}
                    compact={compact}
                    invalid={invalid}
                  />
              )}
              plugins={[
                  <TimePicker key="time" position="bottom" />,
                  <DateTimeFooter
                    key="footer"
                    position="bottom"
                    onCancel={close}
                    onApply={apply}
                    applyDisabled={!draft}
                  />,
              ]}
            />
        </div>
    );
};

export default DateTimePicker;
